import React, { useMemo, useState } from 'react';
import { Card, Table, Button, Space, Tag, Select, App, Popconfirm, Tooltip, Input, DatePicker } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined, LinkOutlined, EyeOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs, { type Dayjs } from 'dayjs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supplierInquiryService, type SupplierInquiry } from '@/services/supplierInquiry.service';

const { RangePicker } = DatePicker;

const SupplierInquiriesPage: React.FC = () => {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [searchText, setSearchText] = useState('');
  const [supplierFilter, setSupplierFilter] = useState<string | undefined>(undefined);
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);

  const { data: list = [], isLoading } = useQuery({
    queryKey: ['supplier-inquiries', statusFilter],
    queryFn: () => supplierInquiryService.list({ status: statusFilter || undefined }),
  });

  const supplierOptions = useMemo(() => {
    const m = new Map<string, string>();
    for (const inq of list) {
      for (const ins of inq.inquirySuppliers ?? []) {
        if (ins.supplier_id && !m.has(ins.supplier_id)) {
          m.set(ins.supplier_id, ins.supplier?.supplier_name ?? ins.supplier_id);
        }
      }
    }
    return Array.from(m, ([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [list]);

  const filteredList = useMemo(() => {
    const term = searchText.trim().toLowerCase();
    const [from, to] = dateRange ?? [null, null];
    return list.filter((inq) => {
      if (supplierFilter && !(inq.inquirySuppliers ?? []).some((s) => s.supplier_id === supplierFilter)) return false;
      if (from || to) {
        if (!inq.inquiry_date) return false;
        const d = dayjs(inq.inquiry_date);
        if (from && d.isBefore(from, 'day')) return false;
        if (to && d.isAfter(to, 'day')) return false;
      }
      if (!term) return true;
      const haystack = [
        inq.inquiry_number,
        inq.status,
        inq.currency,
        inq.remarks,
        ...(inq.inquirySuppliers ?? []).flatMap((s) => [s.supplier?.supplier_name, s.supplier?.supplier_code, s.supplier_email]),
        ...(inq.lines ?? []).flatMap((l) => [l.item_code, l.item_description, l.project?.project_number, l.project?.project_name]),
      ];
      return haystack.some((v) => v && String(v).toLowerCase().includes(term));
    });
  }, [list, searchText, supplierFilter, dateRange]);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => supplierInquiryService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supplier-inquiries'] });
      message.success('Supplier inquiry deleted');
    },
    onError: (e: any) => {
      message.error(e?.response?.data?.message || e?.message || 'Delete failed');
    },
  });

  const copyLink = async (inquirySupplierId: string) => {
    try {
      const { link } = await supplierInquiryService.getLink(inquirySupplierId);
      await navigator.clipboard.writeText(link);
      message.success('Link copied to clipboard');
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Failed to get link');
    }
  };

  const hasFilters = Boolean(searchText || statusFilter || supplierFilter || dateRange);

  const columns: ColumnsType<SupplierInquiry> = [
    {
      title: 'Inquiry No',
      dataIndex: 'inquiry_number',
      key: 'inquiry_number',
      width: 140,
      sorter: (a, b) => String(a.inquiry_number ?? '').localeCompare(String(b.inquiry_number ?? '')),
    },
    {
      title: 'Date',
      dataIndex: 'inquiry_date',
      key: 'inquiry_date',
      width: 110,
      sorter: (a, b) => dayjs(a.inquiry_date ?? 0).valueOf() - dayjs(b.inquiry_date ?? 0).valueOf(),
      render: (d: string) => (d ? dayjs(d).format('DD MMM YYYY') : '-'),
    },
    {
      title: 'Deadline',
      dataIndex: 'response_deadline',
      key: 'response_deadline',
      width: 110,
      sorter: (a, b) => dayjs(a.response_deadline ?? 0).valueOf() - dayjs(b.response_deadline ?? 0).valueOf(),
      render: (d: string) => (d ? dayjs(d).format('DD MMM YYYY') : '-'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (s: string) => {
        const color = s === 'Sent' || s === 'Open' ? 'blue' : s === 'Closed' ? 'default' : 'green';
        return <Tag color={color}>{s}</Tag>;
      },
    },
    {
      title: 'Suppliers',
      key: 'suppliers',
      width: 220,
      render: (_, r) => {
        const names = (r.inquirySuppliers ?? []).map((s) => s.supplier?.supplier_name ?? s.supplier_id);
        if (!names.length) return '-';
        const label = names.length === 1 ? names[0] : `${names[0]} +${names.length - 1} more`;
        return (
          <Tooltip title={names.join(', ')}>
            <span>{label}</span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 220,
      render: (_, record) => (
        <Space size="small" wrap>
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => navigate(`/scm/procurement/supplier-inquiries/${record.id}`)}>
            View
          </Button>
          {record.inquirySuppliers?.map((ins) => (
            <Button key={ins.id} type="link" size="small" icon={<LinkOutlined />} onClick={() => copyLink(ins.id)} title={`Copy inquiry link for ${ins.supplier?.supplier_name ?? 'supplier'}`}>
              Link
            </Button>
          ))}
          {(record.active_quotation_count ?? 0) > 0 ? (
            <Tooltip title="Cannot delete: supplier quotation(s) exist against this inquiry. Delete those quotations first.">
              <span>
                <Button type="link" size="small" danger icon={<DeleteOutlined />} disabled>
                  Delete
                </Button>
              </span>
            </Tooltip>
          ) : (
            <Popconfirm
              title="Delete this inquiry?"
              description="Only allowed if there are no quotations, bid analyses, or purchase orders. This cannot be undone."
              okText="Delete"
              okButtonProps={{ danger: true }}
              disabled={deleteMutation.isPending}
              onConfirm={() => deleteMutation.mutate(record.id)}
            >
              <Button type="link" size="small" danger icon={<DeleteOutlined />} loading={deleteMutation.isPending}>
                Delete
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="Supplier Inquiries"
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/scm/procurement/supplier-inquiries/new')}>
          Create Inquiry
        </Button>
      }
    >
      <Space wrap style={{ marginBottom: 16 }}>
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Search inquiry #, supplier, item, project, remarks"
          style={{ width: 340 }}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
        <Select
          placeholder="Supplier"
          allowClear
          showSearch
          optionFilterProp="label"
          style={{ width: 240 }}
          value={supplierFilter}
          onChange={setSupplierFilter}
          options={supplierOptions}
        />
        <Select
          placeholder="Status"
          allowClear
          style={{ width: 130 }}
          value={statusFilter || undefined}
          onChange={(v) => setStatusFilter(v ?? '')}
          options={[
            { value: 'Draft', label: 'Draft' },
            { value: 'Sent', label: 'Sent' },
            { value: 'Open', label: 'Open' },
            { value: 'Closed', label: 'Closed' },
          ]}
        />
        <RangePicker
          value={dateRange}
          onChange={(v) => setDateRange(v as [Dayjs | null, Dayjs | null] | null)}
          format="DD MMM YYYY"
          allowEmpty={[true, true]}
        />
        {hasFilters ? (
          <Button
            onClick={() => {
              setSearchText('');
              setStatusFilter('');
              setSupplierFilter(undefined);
              setDateRange(null);
            }}
          >
            Clear filters
          </Button>
        ) : null}
      </Space>
      <Table
        rowKey="id"
        columns={columns}
        dataSource={filteredList}
        loading={isLoading}
        pagination={{
          defaultPageSize: 20,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          showTotal: (total) => `${total} inquir${total === 1 ? 'y' : 'ies'}`,
        }}
      />
    </Card>
  );
};

export default SupplierInquiriesPage;
